import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { NodeSelection } from '@tiptap/pm/state';
import katex from 'katex';

// 行内公式扩展（保持原样，可以和普通文本一起选中）
export const InlineMath = Node.create({
    name: 'inlineMath',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
        return {
            latex: {
                default: '',
            },
            blockId: {
                default: null,
            },
            detailId: {
                default: null,
            },
            isProtected: {
                default: false,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="inline-math"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const latex = HTMLAttributes.latex || '';
        const blockId = HTMLAttributes.blockId;
        const detailId = HTMLAttributes.detailId;
        let renderedHTML = '';

        try {
            renderedHTML = katex.renderToString(latex, {
                throwOnError: false,
                displayMode: false,
            });
        } catch (error) {
            renderedHTML = latex;
        }

        const attrs = {
            'data-type': 'inline-math',
            'data-latex': latex,
            class: 'inline-math',
        };
        if (blockId) {
            attrs['data-block-id'] = blockId;
        }
        if (detailId) {
            attrs['data-detail-id'] = detailId;
        }

        return [
            'span',
            mergeAttributes(HTMLAttributes, attrs),
            ['span', { class: 'katex-wrapper' }, renderedHTML],
        ];
    },

    addNodeView() {
        return ({ node }) => {
            const dom = document.createElement('span');
            dom.className = 'inline-math';
            dom.setAttribute('data-type', 'inline-math');
            dom.setAttribute('data-latex', node.attrs.latex);
            if (node.attrs.blockId) {
                dom.setAttribute('data-block-id', node.attrs.blockId);
            }
            if (node.attrs.detailId) {
                dom.setAttribute('data-detail-id', node.attrs.detailId);
            }
            if (node.attrs.isProtected) {
                dom.setAttribute('data-protected', 'true');
                dom.classList.add('tiptap-protected');
            }

            const wrapper = document.createElement('span');
            wrapper.className = 'katex-wrapper';

            try {
                katex.render(node.attrs.latex, wrapper, {
                    throwOnError: false,
                    displayMode: false,
                });
            } catch (error) {
                wrapper.textContent = node.attrs.latex;
            }

            dom.appendChild(wrapper);

            return {
                dom,
                update: (updatedNode) => {
                    if (updatedNode.type !== this.type) {
                        return false;
                    }

                    wrapper.innerHTML = '';
                    try {
                        katex.render(updatedNode.attrs.latex, wrapper, {
                            throwOnError: false,
                            displayMode: false,
                        });
                    } catch (error) {
                        wrapper.textContent = updatedNode.attrs.latex;
                    }

                    return true;
                },
            };
        };
    },
});

// 块级公式扩展（添加保护机制，不能和普通文本一起选中）
export const BlockMath = Node.create({
    name: 'blockMath',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: false,

    addAttributes() {
        return {
            latex: {
                default: '',
            },
            blockId: {
                default: null,
            },
            detailId: {
                default: null,
            },
            isProtected: {
                default: false,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="block-math"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const latex = HTMLAttributes.latex || '';
        const blockId = HTMLAttributes.blockId;
        const detailId = HTMLAttributes.detailId;
        let renderedHTML = '';

        try {
            renderedHTML = katex.renderToString(latex, {
                throwOnError: false,
                displayMode: true,
            });
        } catch (error) {
            renderedHTML = latex;
        }

        const attrs = {
            'data-type': 'block-math',
            'data-latex': latex,
            class: 'block-math',
        };
        if (blockId) {
            attrs['data-block-id'] = blockId;
        }
        if (detailId) {
            attrs['data-detail-id'] = detailId;
        }

        return [
            'div',
            mergeAttributes(HTMLAttributes, attrs),
            ['div', { class: 'katex-wrapper' }, renderedHTML],
        ];
    },

    // 阻止键盘操作删除或修改公式
    addKeyboardShortcuts() {
        return {
            // 阻止删除公式
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                // 检查是否选中了公式节点
                if (
                    selection instanceof NodeSelection &&
                    selection.node.type.name === 'blockMath'
                ) {
                    return true; // 阻止删除
                }

                // 检查选区是否包含公式
                let hasBlockMath = false;
                state.doc.nodesBetween(selection.from, selection.to, (node) => {
                    if (node.type.name === 'blockMath') {
                        hasBlockMath = true;
                        return false;
                    }
                });

                if (hasBlockMath) {
                    return true; // 阻止操作
                }

                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                // 检查是否选中了公式节点
                if (
                    selection instanceof NodeSelection &&
                    selection.node.type.name === 'blockMath'
                ) {
                    return true; // 阻止删除
                }

                // 检查选区是否包含公式
                let hasBlockMath = false;
                state.doc.nodesBetween(selection.from, selection.to, (node) => {
                    if (node.type.name === 'blockMath') {
                        hasBlockMath = true;
                        return false;
                    }
                });

                if (hasBlockMath) {
                    return true; // 阻止操作
                }

                return false;
            },
            // 阻止剪切公式
            'Mod-x': ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                // 检查是否选中了公式节点
                if (
                    selection instanceof NodeSelection &&
                    selection.node.type.name === 'blockMath'
                ) {
                    return true; // 阻止剪切
                }

                let hasBlockMath = false;
                state.doc.nodesBetween(selection.from, selection.to, (node) => {
                    if (node.type.name === 'blockMath') {
                        hasBlockMath = true;
                        return false;
                    }
                });

                if (hasBlockMath) {
                    return true; // 阻止剪切
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        const extensionThis = this;

        return [
            new Plugin({
                key: new PluginKey('preventBlockMathModification'),
                filterTransaction: (transaction, state) => {
                    // 如果事务改变了文档
                    // 允许带有 'allowFormulaModification' 标记的事务通过（用于公式重写功能）
                    // 带 allowChapterReplace 的事务是章节改稿整体替换（旧公式随旧正文一起换掉），同样放行
                    if (
                        transaction.docChanged &&
                        !transaction.getMeta('paste') &&
                        !transaction.getMeta('allowFormulaModification') &&
                        !transaction.getMeta('allowChapterReplace')
                    ) {
                        let shouldBlock = false;

                        // 检查是否有公式节点被删除或修改
                        state.doc.descendants((node) => {
                            if (node.type.name === 'blockMath') {
                                // 找到该公式在新文档中的位置
                                let foundInNew = false;
                                let contentChanged = false;

                                transaction.doc.descendants((newNode) => {
                                    if (
                                        newNode.type.name === 'blockMath' &&
                                        newNode.attrs.blockId === node.attrs.blockId
                                    ) {
                                        foundInNew = true;
                                        // 检查内容是否被修改
                                        if (newNode.attrs.latex !== node.attrs.latex) {
                                            contentChanged = true;
                                        }
                                        return false;
                                    }
                                });

                                // 如果公式被删除或内容被修改，阻止事务
                                if (!foundInNew || contentChanged) {
                                    shouldBlock = true;
                                    return false;
                                }
                            }
                        });

                        if (shouldBlock) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    handleDOMEvents: {
                        // 点击公式时整块选中
                        mousedown: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                try {
                                    const $pos = view.state.doc.resolve(pos.pos);

                                    // 检查点击位置的节点
                                    const nodeAtPos = view.state.doc.nodeAt(pos.pos);
                                    if (nodeAtPos && nodeAtPos.type.name === 'blockMath') {
                                        // 创建 NodeSelection 选中整个公式
                                        const selection = NodeSelection.create(
                                            view.state.doc,
                                            pos.pos,
                                        );
                                        view.dispatch(view.state.tr.setSelection(selection));
                                        event.preventDefault();
                                        return true;
                                    }

                                    // 检查父节点
                                    for (let depth = $pos.depth; depth >= 0; depth--) {
                                        const node = $pos.node(depth);
                                        if (node.type.name === 'blockMath') {
                                            const nodePos = $pos.before(depth);
                                            const selection = NodeSelection.create(
                                                view.state.doc,
                                                nodePos,
                                            );
                                            view.dispatch(view.state.tr.setSelection(selection));
                                            event.preventDefault();
                                            return true;
                                        }
                                    }
                                } catch (e) {
                                    // 忽略错误
                                }
                            }
                            return false;
                        },
                        // 阻止拖拽公式
                        dragstart: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                try {
                                    const $pos = view.state.doc.resolve(pos.pos);

                                    // 检查是否在公式内
                                    for (let depth = $pos.depth; depth >= 0; depth--) {
                                        const node = $pos.node(depth);
                                        if (node.type.name === 'blockMath') {
                                            event.preventDefault();
                                            return true;
                                        }
                                    }

                                    // 检查点击位置的节点
                                    const nodeAtPos = view.state.doc.nodeAt(pos.pos);
                                    if (nodeAtPos && nodeAtPos.type.name === 'blockMath') {
                                        event.preventDefault();
                                        return true;
                                    }
                                } catch (e) {
                                    // 忽略错误
                                }
                            }
                            return false;
                        },
                    },
                },
            }),
        ];
    },

    addNodeView() {
        return ({ node }) => {
            const dom = document.createElement('div');
            dom.className = 'block-math';
            dom.setAttribute('data-type', 'block-math');
            dom.setAttribute('data-latex', node.attrs.latex);
            // 设置不可编辑属性
            dom.contentEditable = 'false';
            dom.style.cursor = 'pointer';
            dom.style.userSelect = 'none';

            if (node.attrs.blockId) {
                dom.setAttribute('data-block-id', node.attrs.blockId);
            }
            if (node.attrs.detailId) {
                dom.setAttribute('data-detail-id', node.attrs.detailId);
            }
            if (node.attrs.isProtected) {
                dom.setAttribute('data-protected', 'true');
                dom.classList.add('tiptap-protected');
            }

            const wrapper = document.createElement('div');
            wrapper.className = 'katex-wrapper';

            try {
                katex.render(node.attrs.latex, wrapper, {
                    throwOnError: false,
                    displayMode: true,
                });
            } catch (error) {
                wrapper.textContent = node.attrs.latex;
            }

            dom.appendChild(wrapper);

            return {
                dom,
                update: (updatedNode) => {
                    if (updatedNode.type !== this.type) {
                        return false;
                    }

                    wrapper.innerHTML = '';
                    try {
                        katex.render(updatedNode.attrs.latex, wrapper, {
                            throwOnError: false,
                            displayMode: true,
                        });
                    } catch (error) {
                        wrapper.textContent = updatedNode.attrs.latex;
                    }

                    return true;
                },
            };
        };
    },
});
